import { Component, Inject, OnInit, SimpleChanges, ChangeDetectorRef, OnChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/services/language.service';
import { ReportService } from 'src/app/sharedModule/report-v2/services/report.service';
import { TranslateService } from '@ngx-translate/core';

interface DialogData {
  llmAnalysis: string;
  isNewAnalysis: boolean;
  isLoading: boolean;
  submissionId: string;
  uploaderName?: string;
  isOnIA?: boolean;
  author?: string;
  title?: string;
  analysisType?: string;
  anonymous?: boolean;
}

interface AnalysisFlag {
  flag: string;
  details: string;
  severity: 'high' | 'medium' | 'low';
}

interface ParsedAnalysis {
  summary: string;
  flags_identified: AnalysisFlag[];
  language?: string;
}

export interface StreamingState {
  analysisSummary: string;
  currentFlags: Array<{ flag: string, details: string }>;
  isStreaming: boolean;
  showFlags: boolean;
  isTranslating: boolean;
}

@Component({
  selector: 'app-ai-analysis',
  templateUrl: './ai_analysis.component.html',
  styleUrls: ['./ai_analysis.component.scss']
})
export class AiAnalysisComponent implements OnInit, OnChanges {
  llmAnalysis!: string;
  analysisSummary!: string;
  flags_identified!: AnalysisFlag[];
  isTyping: boolean = false;
  displayedText: string = '';
  baseTypingSpeed: number = 75;
  isLoading: boolean;
  parsedAnalysis?: ParsedAnalysis;
  displayedFlags: AnalysisFlag[] = [];
  isParsingError: boolean = false;
  streamingState: StreamingState = {
    analysisSummary: '',
    currentFlags: [],
    isStreaming: false,
    showFlags: false,
    isTranslating: false
  };
  private currentLanguage: string;
  private translatedContent: boolean = false;
  private languageSubscription: Subscription;

  submissionId: string;
  uploaderName?: string;
  isOnIA?: boolean;
  author?: string;
  title?: string;
  analysisType?: string;
  constructor(
    private dialogRef: MatDialogRef<AiAnalysisComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private cdr: ChangeDetectorRef,
    private languageService: LanguageService,
    private reportService: ReportService,
    private translate: TranslateService
  ) {
    // Get current language
    const currentLang = this.translate.currentLang || localStorage.getItem('websiteLanguage') || 'en';
    this.translate.use(currentLang);

    this.isLoading = data.isLoading ?? true;
    this.submissionId = data.submissionId;
    this.uploaderName = data.uploaderName;
    this.isOnIA = data.isOnIA;
    this.author = data.author;
    this.title = data.title;
    this.analysisType = data.analysisType;
    // Validate required fields
    if (!this.submissionId) {
      console.error('submissionId is required but was not provided');
    }
    if (!this.author) {
      console.error('author is required but was not provided');
    }
  }

  ngOnInit() {
    if (this.data.llmAnalysis) {
      this.isLoading = false;
      this.llmAnalysis = this.data.llmAnalysis;
      this.parseAnalysis().then(() => {
        if (this.data.isNewAnalysis) {
          this.simulateTyping();
        } else {
          this.displayedText = this.analysisSummary;
          if (this.parsedAnalysis) {
            this.displayedFlags = this.parsedAnalysis.flags_identified;
          }
        }
        this.streamContent();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && changes['data'].currentValue?.llmAnalysis) {
      this.isLoading = false;
      this.llmAnalysis = this.data.llmAnalysis;
      this.parseAnalysis().then(() => {
        this.simulateTyping();
      });
    }
  }

  private async translateAnalysis(text: string): Promise<string> {
    try {
      // Map Norwegian language codes
      if (this.currentLanguage == 'no_bokmal') {
        this.currentLanguage = 'nb-NO';
      } else if (this.currentLanguage == 'no_nynorsk') {
        this.currentLanguage = 'nn-NO';
      }

      const response = await this.languageService.translateTextWithGoogle(text, this.currentLanguage)
        .toPromise();

      return response.translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }

  async parseAnalysis() {
    if (!this.llmAnalysis) {
      console.error('No analysis data to parse');
      return;
    }

    try {
      // Special handling for stylometric analysis which might not be in JSON format
      if (this.analysisType === 'stylometric') {
        try {
          // First attempt to parse as JSON (new format)
          const cleanJson = this.llmAnalysis
            .replace(/```json/g, '')
            .replace(/```/g, '')
            .trim();

          this.parsedAnalysis = JSON.parse(cleanJson);

          // If no summary or flags, format the data ourselves
          if (!this.parsedAnalysis.summary) {
            // Check if data is in a different format (direct text analysis)
            this.parseLegacyStylometricData();
          }
        } catch (error) {
          console.warn('JSON parsing failed, trying legacy format:', error);
          this.parseLegacyStylometricData();
        }
      } else {
        // Regular metadata analysis - standard JSON parsing
        const cleanJson = this.llmAnalysis
          .replace(/```json/g, '')
          .replace(/```/g, '')
          .trim();

        this.parsedAnalysis = JSON.parse(cleanJson);
      }

      // If no language is specified in the analysis, default to 'en'
      if (!this.parsedAnalysis.language) {
        this.parsedAnalysis.language = 'en';
      }

      // Set initial values before translation
      this.analysisSummary = this.parsedAnalysis.summary;
      this.flags_identified = this.parsedAnalysis.flags_identified || [];

      if (this.data.anonymous === true) {
        this.flags_identified = this.flags_identified.filter(
          f => !f.flag.toLowerCase().includes('author')
        );
        this.parsedAnalysis.flags_identified = this.flags_identified;
      }

      // Now handle translation if needed
      await this.handleTranslation();
    } catch (error) {
      console.error('Error parsing analysis:', error);
      this.isParsingError = true;
      // For complete failure, just show raw text
      this.analysisSummary = typeof this.llmAnalysis === 'string'
        ? this.llmAnalysis
        : 'Error parsing analysis data';
      this.flags_identified = [];
    }
  }

  // Handle legacy or plain text stylometric data
  private parseLegacyStylometricData(): void {
    // For non-JSON data or old format, create our own structure
    let analysis: string;

    if (typeof this.llmAnalysis === 'string') {
      analysis = this.llmAnalysis;
    } else if (typeof this.llmAnalysis === 'object') {
      // Try to extract useful text from the object
      analysis = JSON.stringify(this.llmAnalysis, null, 2);
    } else {
      analysis = 'Unable to interpret analysis data';
    }

    // Split the analysis into sections
    const sections = analysis.split(/\n\s*\n/);

    // Use the first section as summary
    const summary = sections[0] || 'Stylometric Analysis';

    // Extract potential flags from the remaining sections
    const flags: AnalysisFlag[] = [];

    for (let i = 1; i < sections.length; i++) {
      const section = sections[i].trim();
      if (section) {
        // Try to extract flag and details
        const lines = section.split('\n');
        if (lines.length > 0) {
          const flag = lines[0].replace(/^[#*-]+\s*/, '').trim();
          const details = lines.slice(1).join(' ').trim();

          if (flag) {
            flags.push({
              flag,
              details: details || 'No additional details provided',
              severity: 'medium'
            });
          }
        }
      }
    }

    this.parsedAnalysis = {
      summary,
      flags_identified: flags,
      language: 'en'
    };
  }

  private async handleTranslation() {
    if (!this.parsedAnalysis) {
      return;
    }

    return new Promise<void>((resolve) => {
      if (this.languageSubscription) {
        this.languageSubscription.unsubscribe();
      }

      this.languageSubscription = this.languageService.selectedLanguage$.subscribe(async (lang) => {

        this.currentLanguage = lang;

        if (lang !== this.parsedAnalysis.language) {
          try {
            this.streamingState.isTranslating = true;
            this.cdr.detectChanges();

            // Use a single key for all translations of this submission
            const storageKey = `llm-analysis-${this.submissionId}`;
            const savedTranslations = localStorage.getItem(storageKey);
            const translations = savedTranslations ? JSON.parse(savedTranslations) : {};

            if (translations[lang]) {
              // Use existing translation for this language
              const translatedAnalysis = translations[lang];
              this.parsedAnalysis = translatedAnalysis;
              this.analysisSummary = translatedAnalysis.summary;
              this.flags_identified = translatedAnalysis.flags_identified;
              this.displayedText = translatedAnalysis.summary;
              this.displayedFlags = translatedAnalysis.flags_identified;
            } else {
              // Perform new translation
              const translatedSummary = await this.translateAnalysis(
                this.parsedAnalysis.summary
              );

              const translatedFlags = await Promise.all(
                this.parsedAnalysis.flags_identified.map(async flag => ({
                  ...flag,
                  flag: await this.translateAnalysis(flag.flag),
                  details: await this.translateAnalysis(flag.details)
                }))
              );

              const translatedAnalysis = {
                summary: translatedSummary,
                flags_identified: translatedFlags,
                language: lang
              };

              // Update UI with new translation
              this.parsedAnalysis = translatedAnalysis;
              this.analysisSummary = translatedAnalysis.summary;
              this.flags_identified = translatedAnalysis.flags_identified;
              this.displayedText = translatedAnalysis.summary;
              this.displayedFlags = translatedAnalysis.flags_identified;

              // Save new translation alongside existing ones
              translations[lang] = translatedAnalysis;
              localStorage.setItem(storageKey, JSON.stringify(translations));

              // Still update backend for persistence
              await this.reportService.updateLLMAnalysis(
                this.submissionId || '',
                this.author || '',
                lang,
                JSON.stringify(translatedAnalysis)
              ).toPromise();
            }

          } catch (error) {
            console.error('Translation error:', error);
          } finally {
            this.streamingState.isTranslating = false;
            this.cdr.detectChanges();
          }
        }
        resolve();
      });
    });
  }

  private simulateTyping(): void {
    this.isTyping = true;
    this.displayedText = '';
    this.displayedFlags = [];

    this.typeText(this.analysisSummary).then(() => {
      if (this.parsedAnalysis) {
        this.animateFlags();
      }
    });
  }

  private typeText(text: string): Promise<void> {
    return new Promise((resolve) => {
      const words = text.split(' ');
      let currentIndex = 0;

      const typeNextWord = () => {
        if (currentIndex < words.length) {
          this.displayedText += (currentIndex > 0 ? ' ' : '') + words[currentIndex];
          currentIndex++;
          setTimeout(typeNextWord, this.getRandomDelay());
        } else {
          resolve();
        }
      };

      typeNextWord();
    });
  }

  private animateFlags(): void {
    if (!this.parsedAnalysis) return;

    let currentIndex = 0;
    const addNextFlag = () => {
      if (currentIndex < this.parsedAnalysis!.flags_identified.length) {
        this.displayedFlags.push(this.parsedAnalysis!.flags_identified[currentIndex]);
        currentIndex++;
        setTimeout(addNextFlag, 500);
      } else {
        this.isTyping = false;
      }
    };

    addNextFlag();
  }

  private getRandomDelay(): number {
    if (Math.random() < 0.15) {
      return 300 + Math.floor(Math.random() * 200);
    }
    const variationFactor = 0.1 + (Math.random() * 1.1);
    return Math.floor(this.baseTypingSpeed * variationFactor);
  }

  closeDialog() {
    this.resetTranslationState();
    this.dialogRef.close();
  }

  async updateDialogData(newData: DialogData) {
    this.isLoading = newData.isLoading ?? true;
    this.cdr.detectChanges();

    try {
      this.data = newData;
      this.llmAnalysis = newData.llmAnalysis;

      if (this.llmAnalysis) {
        await this.parseAnalysis();

        // Reset streaming state before starting new stream
        this.streamingState = {
          analysisSummary: '',
          currentFlags: [],
          isStreaming: false,
          showFlags: false,
          isTranslating: false
        };

        this.isLoading = false;
        this.cdr.detectChanges();

        if (newData.isNewAnalysis) {
          await this.streamContent();
        } else {
          // For cached data, display immediately
          this.streamingState.analysisSummary = this.analysisSummary || '';
          this.streamingState.currentFlags = this.flags_identified || [];
          this.streamingState.showFlags = true;
          this.cdr.detectChanges();
        }
      }
    } catch (error) {
      console.error('Error updating dialog data:', error);
      this.isLoading = false;
    } finally {
      this.cdr.detectChanges();
    }
  }

  close() {
    this.resetTranslationState();
    this.dialogRef.close();
  }

  private async streamContent() {
    if (!this.analysisSummary || !this.flags_identified) {
      console.error('Analysis data is not ready for streaming');
      return;
    }

    // Reset streaming state at the start
    this.streamingState = {
      analysisSummary: '',
      currentFlags: [],
      isStreaming: true,
      showFlags: false,
      isTranslating: false
    };
    this.cdr.detectChanges();

    try {
      // Stream summary
      const words = this.analysisSummary.split(' ');
      for (let i = 0; i < words.length; i++) {
        const burstSize = Math.floor(Math.random() * 4) + 2;

        for (let j = 0; j < burstSize && (i + j) < words.length; j++) {
          await new Promise(resolve => setTimeout(resolve, Math.random() * 5 + 15));
          this.streamingState.analysisSummary += words[i + j] + ' ';
          this.cdr.detectChanges();
        }

        i += burstSize - 1;

        if (i < words.length - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.random() * 40 + 30));
        }

        if (Math.random() < 0.1 && i < words.length - 5) {
          await new Promise(resolve => setTimeout(resolve, Math.random() * 100 + 50));
        }
      }

      // Stream flags
      await new Promise(resolve => setTimeout(resolve, 150));
      this.streamingState.showFlags = true;
      this.streamingState.currentFlags = [];
      this.cdr.detectChanges();

      for (const flag of this.flags_identified) {
        await new Promise(resolve => setTimeout(resolve, 80));

        const newFlag = { flag: flag.flag, details: '' };
        this.streamingState.currentFlags.push(newFlag);
        this.cdr.detectChanges();

        const detailWords = flag.details.split(' ');
        for (const word of detailWords) {
          await new Promise(resolve => setTimeout(resolve, Math.random() * 10 + 20));
          newFlag.details += word + ' ';
          this.cdr.detectChanges();

          if (Math.random() < 0.2) {
            await new Promise(resolve => setTimeout(resolve, Math.random() * 30 + 20));
          }
        }
      }
    } catch (error) {
      console.error('Error during content streaming:', error);
    } finally {
      this.streamingState.isStreaming = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    this.resetTranslationState();
  }

  // Add reset method to clear translation state
  private resetTranslationState() {
    this.translatedContent = false;
  }
}
